import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import type { Store } from '@warp-drive/core';
import { saveRecord } from 'tio-data/requests/save-record';
import { beginMatchParticipantEnrollment } from 'tio-data/requests/match-participant';
import type { AuthenticatedSyfEnrollmentRouteModel } from 'tio-employee/routes/authenticated/syf/enrollment';

import type EmployeeService from 'tio-employee/services/employee';
import SyfEnrollment from 'tio-employee/components/syf/enrollment';

interface SyfEnrollmentRouteSignature {
  Args: {
    model: AuthenticatedSyfEnrollmentRouteModel;
  };
}

export default class SyfAccountEnrollmentRouteComponent extends Component<SyfEnrollmentRouteSignature> {
  @service declare router: RouterService;
  @service declare employee: EmployeeService;
  @service declare store: Store;

  @tracked beginEnrollmentLoading = false;

  transitionToNextStep = () => {
    if (this.args.model.person.activeAccounts.length > 0) {
      this.router.transitionTo('authenticated.syf.account-confirmation');
    } else {
      this.router.transitionTo('authenticated.observability.upload', {
        queryParams: { source: 'syf', providers: 'statement,nslds' },
      });
    }
  };

  didBeginEnrollment = async () => {
    const matchParticipant = this.args.model.matchParticipant;
    if (!matchParticipant) {
      console.error('Missing valid MatchParticipant for enrollment');
      return;
    }

    if (matchParticipant.isEnrolling) {
      this.transitionToNextStep();
    } else {
      this.beginEnrollmentLoading = true;
      try {
        matchParticipant.person = this.args.model.person;
        await saveRecord(this.store, matchParticipant);
        await beginMatchParticipantEnrollment(this.store, matchParticipant);
        this.transitionToNextStep();
      } catch {
        this.beginEnrollmentLoading = false;
      }
    }
  };

  <template>
    <SyfEnrollment
      @onBeginEnrollment={{this.didBeginEnrollment}}
      @beginEnrollmentLoading={{this.beginEnrollmentLoading}}
      @matchPlan={{@model.matchPlan}}
    />
  </template>
}
